/**
 * iTwin Entity Highlighting and Property Display Utilities
 */

/**
 * Format property values for display (handle BigInt)
 */
const formatPropertyValue = (value) => {
    if (value === null || value === undefined) {
        return 'N/A';
    }
    // Handle BigInt (common in iTwin)
    if (typeof value === 'bigint') {
        return value.toString();
    }
    if (typeof value === 'object') {
        return JSON.stringify(value, (key, val) => 
            typeof val === 'bigint' ? val.toString() : val
        , 2);
    }
    if (typeof value === 'number') {
        return value.toFixed(4);
    }
    return String(value);
};

/**
 * Convert entity ID from various formats (hex string, decimal string, BigInt) to comparable format
 * @param {string|bigint|number} entityId - Entity ID in any format
 * @returns {object} Object with string and numeric representations
 */
const normalizeEntityId = (entityId) => {
    let numericValue;
    
    if (typeof entityId === 'bigint') {
        numericValue = entityId;
    } else if (typeof entityId === 'string') {
        // Check if it's a hex string (starts with 0x or 0X)
        if (entityId.toLowerCase().startsWith('0x')) {
            // Convert hex to decimal
            numericValue = BigInt(entityId);
        } else {
            // It's a decimal string
            numericValue = BigInt(entityId);
        }
    } else if (typeof entityId === 'number') {
        numericValue = BigInt(Math.floor(entityId));
    } else {
        console.warn('Unknown entity ID format:', entityId);
        numericValue = BigInt(0);
    }
    
    return {
        asString: numericValue.toString(), // Decimal string
        asNumber: numericValue,             // BigInt
        asHex: '0x' + numericValue.toString(16), // Hex string (for display)
        original: entityId
    };
};

/**
 * Highlights an entity in the iTwin tileset by its element ID
 * Supports hex strings (0x20000015f62) and decimal strings/BigInt
 */
export const highlightITwinEntity = (tileset, entityId, highlightColor) => {
    if (!tileset || !entityId) {
        console.warn('Tileset or entity ID not provided');
        return;
    }

    const color = highlightColor || window.Cesium.Color.fromCssColorString('#00ffff').withAlpha(0.9);
    
    // Normalize the entity ID
    const normalized = normalizeEntityId(entityId);
    
    console.log('🎯 Highlighting entity:', {
        original: normalized.original,
        decimal: normalized.asString,
        hex: normalized.asHex
    });
    
    window.currentHighlightedEntityId = normalized.asString;

    try {
        tileset.style = new window.Cesium.Cesium3DTileStyle({
            color: {
                conditions: [
                    // ✅ ONLY use === (strict equality)
                    [`\${feature['element']} === ${normalized.asString}`, `color('${window.Cesium.Color.toCssColorString(color)}', 1.0)`],
                    [`\${feature['ECInstanceId']} === ${normalized.asString}`, `color('${window.Cesium.Color.toCssColorString(color)}', 1.0)`],
                    [`\${feature['elementId']} === ${normalized.asString}`, `color('${window.Cesium.Color.toCssColorString(color)}', 1.0)`],
                    // Default: white
                    ['true', 'color("#FFFFFF", 1.0)']
                ]
            },
            show: true
        });

        console.log(`✅ Highlighted element: ${normalized.asString} (hex: ${normalized.asHex})`);
    } catch (error) {
        console.error('❌ Failed to highlight entity:', error);
        throw error;
    }
};

/**
 * Highlights multiple entities with different colors
 */
export const highlightMultipleITwinEntities = (tileset, entities) => {
    if (!tileset || !entities || entities.length === 0) {
        console.warn('Tileset or entities not provided');
        return;
    }

    console.log(`🎨 Highlighting ${entities.length} entities with different colors`);

    // Build conditions array with proper BigInt handling
    const conditions = [];
    
    entities.forEach((entity, index) => {
        const color = entity.color || '#00d4ff'; // Default to cyan
        const normalized = normalizeEntityId(entity.entityId);
        
        console.log(`   Entity ${index + 1}:`, {
            id: normalized.asString,
            hex: normalized.asHex,
            color: color
        });
        
        // ✅ ONLY use === (strict equality)
        conditions.push(
            // Primary: 'element' property (numeric)
            [`\${feature['element']} === ${normalized.asString}`, `color('${color}', 1.0)`],
            // Alternative: ECInstanceId
            [`\${feature['ECInstanceId']} === ${normalized.asString}`, `color('${color}', 1.0)`],
            // Alternative: elementId
            [`\${feature['elementId']} === ${normalized.asString}`, `color('${color}', 1.0)`]
        );
    });

    // Add default condition for non-highlighted entities
    conditions.push(['true', 'color("#FFFFFF", 1.0)']);

    try {
        tileset.style = new window.Cesium.Cesium3DTileStyle({
            color: {
                conditions: conditions
            },
            show: true
        });

        // Store highlighted entity IDs (as strings)
        window.currentHighlightedEntityIds = entities.map(e => String(e.entityId));
        window.currentHighlightedEntitiesMap = new Map(
            entities.map(e => [String(e.entityId), e.color])
        );

        console.log(`✅ Applied style with ${conditions.length} conditions for ${entities.length} entities`);
    } catch (error) {
        console.error('❌ Failed to apply tileset style:', error);
        throw error;
    }
};

/**
 * Highlights multiple entities with the same color
 * @param {Cesium.Cesium3DTileset} tileset - The loaded iTwin tileset
 * @param {Array<string|bigint>} entityIds - Array of entity IDs to highlight
 * @param {string|Cesium.Color} highlightColor - Optional color (default: cyan)
 * @example
 * highlightMultipleITwinEntitiesSameColor(tileset, 
 *   ['2199023410388', '2199023410456', '2199023410789'], 
 *   '#00d4ff'
 * );
 */
export const highlightMultipleITwinEntitiesSameColor = (tileset, entityIds, highlightColor) => {
    if (!tileset || !entityIds || entityIds.length === 0) {
        console.warn('Tileset or entity IDs not provided');
        return;
    }

    console.log(`🎨 Highlighting ${entityIds.length} entities with same color`);

    // Handle color input (string or Cesium.Color)
    let colorString;
    if (highlightColor) {
        colorString = typeof highlightColor === 'string' 
            ? highlightColor 
            : window.Cesium.Color.toCssColorString(highlightColor);
    } else {
        colorString = '#00d4ff'; // Default cyan
    }

    console.log(`   Color: ${colorString}`);
    console.log(`   Entity IDs:`, entityIds.map(id => String(id)));

    // Build condition string for all entity IDs with multiple property attempts
    const conditions = [];
    
    entityIds.forEach(id => {
        const normalized = normalizeEntityId(id);
        
        console.log(`   Normalized ID ${id}:`, {
            decimal: normalized.asString,
            hex: normalized.asHex
        });
        
        // ✅ ONLY use === (strict equality) - Cesium style parser requirement
        // Try 'element' property (most common in iTwin)
        conditions.push(`\${feature['element']} === ${normalized.asString}`);
        
        // Try alternative property names
        conditions.push(`\${feature['ECInstanceId']} === ${normalized.asString}`);
        conditions.push(`\${feature['elementId']} === ${normalized.asString}`);
    });

    // Join all conditions with OR operator
    const entityCondition = conditions.join(' || ');

    console.log(`   Condition count: ${conditions.length}`);
    console.log(`   Condition length: ${entityCondition.length} characters`);
    console.log(`   Sample condition: ${conditions[0]}`);

    try {
        tileset.style = new window.Cesium.Cesium3DTileStyle({
            color: {
                conditions: [
                    [entityCondition, `color('${colorString}', 1.0)`],
                    ['true', 'color("#FFFFFF", 1.0)']
                ]
            },
            show: true
        });

        // Store highlighted entity IDs (as strings)
        window.currentHighlightedEntityIds = entityIds.map(id => String(id));

        console.log(`✅ Highlighted ${entityIds.length} entities with color ${colorString}`);
    } catch (error) {
        console.error('❌ Failed to apply tileset style:', error);
        console.error('   Error details:', {
            message: error.message,
            conditionSample: conditions[0],
            totalConditions: conditions.length
        });
        throw error;
    }
};

/**
 * Highlights multiple entities by directly setting feature colors (same method as click handler)
 * This is more reliable than Cesium3DTileStyle for batched tilesets
 */
export const highlightMultipleITwinEntitiesDirect = (tileset, entityIds, highlightColor) => {
    if (!tileset || !entityIds || entityIds.length === 0) {
        console.warn('Tileset or entity IDs not provided');
        return;
    }

    console.log(`🎨 Direct highlighting ${entityIds.length} entities`);

    // Parse color
    let cesiumColor;
    if (typeof highlightColor === 'string') {
        cesiumColor = window.Cesium.Color.fromCssColorString(highlightColor);
    } else {
        cesiumColor = highlightColor || window.Cesium.Color.fromCssColorString('#00d4ff');
    }

    // Convert entity IDs to Set for fast lookup
    const entityIdSet = new Set(
        entityIds.map(id => {
            const normalized = normalizeEntityId(id);
            return normalized.asString;
        })
    );

    console.log('   Entity ID set:', Array.from(entityIdSet));

    let highlightedCount = 0;
    let checkedFeaturesCount = 0;

    // Function to process a tile's features
    const processTile = (tile) => {
        if (!tile.content || !tile.content.featuresLength) {
            return;
        }

        const content = tile.content;
        const featuresLength = content.featuresLength;

        for (let i = 0; i < featuresLength; i++) {
            checkedFeaturesCount++;
            
            try {
                const feature = content.getFeature(i);
                
                if (!feature) continue;

                // Get element ID from feature
                let featureElementId = null;
                
                if (typeof feature.getProperty === 'function') {
                    // Try to get 'element' property
                    try {
                        const elementProp = feature.getProperty('element');
                        if (elementProp !== undefined && elementProp !== null) {
                            featureElementId = typeof elementProp === 'bigint' 
                                ? elementProp.toString() 
                                : String(elementProp);
                        }
                    } catch (err) {
                        // Try alternative property names
                        try {
                            const altProp = feature.getProperty('ECInstanceId') || feature.getProperty('elementId');
                            if (altProp !== undefined && altProp !== null) {
                                featureElementId = typeof altProp === 'bigint' 
                                    ? altProp.toString() 
                                    : String(altProp);
                            }
                        } catch (err2) {
                            // Silent fail
                        }
                    }
                }

                // Check if this feature should be highlighted
                if (featureElementId && entityIdSet.has(featureElementId)) {
                    // Store original color if not already stored
                    if (!feature._originalColor) {
                        feature._originalColor = feature.color 
                            ? window.Cesium.Color.clone(feature.color) 
                            : window.Cesium.Color.WHITE.clone();
                    }

                    // Apply highlight color
                    feature.color = cesiumColor;
                    highlightedCount++;

                    console.log(`   ✅ Highlighted feature with element ID: ${featureElementId}`);
                }

            } catch (error) {
                // Skip features that can't be accessed
            }
        }
    };

    // Process root tile
    if (tileset.root) {
        processTile(tileset.root);

        // Process child tiles recursively
        const processChildren = (tile) => {
            if (tile.children && tile.children.length > 0) {
                tile.children.forEach(child => {
                    processTile(child);
                    processChildren(child);
                });
            }
        };

        processChildren(tileset.root);
    }

    console.log(`   Checked ${checkedFeaturesCount} features`);
    console.log(`   ✅ Successfully highlighted ${highlightedCount} features`);

    if (highlightedCount === 0) {
        console.warn('   ⚠️ No features were highlighted. Possible reasons:');
        console.warn('      - Entity IDs do not match any loaded features');
        console.warn('      - Tiles containing those features are not loaded yet');
        console.warn('      - Property name is different');
    }

    return highlightedCount;
};

/**
 * Clear all direct highlights and restore original colors
 */
export const clearITwinDirectHighlight = (tileset) => {
    if (!tileset) {
        console.warn('Tileset not provided');
        return;
    }

    console.log('🔄 Clearing direct highlights...');

    let restoredCount = 0;

    const processTile = (tile) => {
        if (!tile.content || !tile.content.featuresLength) {
            return;
        }

        const content = tile.content;
        const featuresLength = content.featuresLength;

        for (let i = 0; i < featuresLength; i++) {
            try {
                const feature = content.getFeature(i);
                
                if (feature && feature._originalColor) {
                    feature.color = feature._originalColor;
                    delete feature._originalColor;
                    restoredCount++;
                }
            } catch (error) {
                // Skip
            }
        }
    };

    if (tileset.root) {
        processTile(tileset.root);

        const processChildren = (tile) => {
            if (tile.children && tile.children.length > 0) {
                tile.children.forEach(child => {
                    processTile(child);
                    processChildren(child);
                });
            }
        };

        processChildren(tileset.root);
    }

    console.log(`   ✅ Restored ${restoredCount} features to original colors`);
};

/**
 * Wrapper that tries direct coloring (more reliable for batched tilesets)
 */
export const highlightMultipleITwinEntitiesSameColordirect = (tileset, entityIds, highlightColor) => {
    console.log('🎨 highlightMultipleITwinEntitiesSameColor called');
    console.log('   Using direct feature coloring method (like click handler)');
    
    return highlightMultipleITwinEntitiesDirect(tileset, entityIds, highlightColor);
};

/**
 * Highlights specific entities and makes all other elements transparent and colorless
 * @param {Cesium.Cesium3DTileset} tileset - The loaded iTwin tileset
 * @param {Array<string>} entityIds - Array of entity IDs to highlight
 * @param {string|Cesium.Color} highlightColor - Color to highlight the specified entities
 * @param {number} transparencyLevel - Transparency level for non-highlighted elements (0.0 to 1.0, default: 0.2)
 * @example
 * highlightMultipleITwinEntitiesWithTransparency(tileset, ['0x123', '0x456'], '#FF0000', 0.2);
 */
export const highlightMultipleITwinEntitiesWithTransparency = (tileset, entityIds, highlightColor, transparencyLevel = 0.2) => {
    if (!tileset || !entityIds || entityIds.length === 0) {
        console.warn('Tileset or entity IDs not provided');
        return;
    }

    console.log(`🎨 Highlighting ${entityIds.length} entities with transparency for others`);

    // Parse highlight color
    let cesiumHighlightColor;
    if (typeof highlightColor === 'string') {
        cesiumHighlightColor = window.Cesium.Color.fromCssColorString(highlightColor);
    } else {
        cesiumHighlightColor = highlightColor || window.Cesium.Color.fromCssColorString('#00d4ff');
    }

    // Create transparent gray color for non-highlighted elements
    const transparentColor = new window.Cesium.Color(0.9, 0.95, 1.0, 0.15);  // ✅ Glass-like: blue-white tint, 85% transparent

    // Convert entity IDs to Set for fast lookup
    const entityIdSet = new Set(
        entityIds.map(id => {
            const normalized = normalizeEntityId(id);
            return normalized.asString;
        })
    );

    console.log('   Entity ID set:', Array.from(entityIdSet));

    let highlightedCount = 0;
    let fadedCount = 0;
    let checkedFeaturesCount = 0;

    // Function to process a tile's features
    const processTile = (tile) => {
        if (!tile.content || !tile.content.featuresLength) {
            return;
        }

        const content = tile.content;
        const featuresLength = content.featuresLength;

        for (let i = 0; i < featuresLength; i++) {
            checkedFeaturesCount++;
            
            try {
                const feature = content.getFeature(i);
                
                if (!feature) continue;

                // Store original color if not already stored
                if (!feature._originalColor) {
                    feature._originalColor = feature.color 
                        ? window.Cesium.Color.clone(feature.color) 
                        : window.Cesium.Color.WHITE.clone();
                }

                // Get element ID from feature
                let featureElementId = null;
                
                if (typeof feature.getProperty === 'function') {
                    // Try to get 'element' property
                    try {
                        const elementProp = feature.getProperty('element');
                        if (elementProp !== undefined && elementProp !== null) {
                            featureElementId = typeof elementProp === 'bigint' 
                                ? elementProp.toString() 
                                : String(elementProp);
                        }
                    } catch (err) {
                        // Try alternative property names
                        try {
                            const altProp = feature.getProperty('ECInstanceId') || feature.getProperty('elementId');
                            if (altProp !== undefined && altProp !== null) {
                                featureElementId = typeof altProp === 'bigint' 
                                    ? altProp.toString() 
                                    : String(altProp);
                            }
                        } catch (err2) {
                            // Silent fail
                        }
                    }
                }

                // Check if this feature should be highlighted or made transparent
                if (featureElementId && entityIdSet.has(featureElementId)) {
                    // Highlight this feature
                    feature.color = cesiumHighlightColor;
                    highlightedCount++;
                    console.log(`   ✅ Highlighted feature with element ID: ${featureElementId}`);
                } else {
                    // Make this feature transparent and colorless
                    feature.color = transparentColor;
                    fadedCount++;
                }

            } catch (error) {
                // Skip features that can't be accessed
            }
        }
    };

    // Process root tile
    if (tileset.root) {
        processTile(tileset.root);

        // Process child tiles recursively
        const processChildren = (tile) => {
            if (tile.children && tile.children.length > 0) {
                tile.children.forEach(child => {
                    processTile(child);
                    processChildren(child);
                });
            }
        };

        processChildren(tileset.root);
    }

    console.log(`   Checked ${checkedFeaturesCount} features`);
    console.log(`   ✅ Successfully highlighted ${highlightedCount} features`);
    console.log(`   🌫️  Made ${fadedCount} features transparent`);

    if (highlightedCount === 0) {
        console.warn('   ⚠️ No features were highlighted. Possible reasons:');
        console.warn('      - Entity IDs do not match any loaded features');
        console.warn('      - Tiles containing those features are not loaded yet');
        console.warn('      - Property name is different');
    }

    return { highlightedCount, fadedCount };
};

/**
 * Restores all iTwin elements to their original colors
 * This is an alias for clearITwinDirectHighlight for better clarity
 * @param {Cesium.Cesium3DTileset} tileset - The loaded iTwin tileset
 * @example
 * restoreOriginalITwinColors(tileset);
 */
export const restoreOriginalITwinColors = (tileset) => {
    console.log('🔄 Restoring original iTwin colors...');
    return clearITwinDirectHighlight(tileset);
};

/**
 * Highlights entities by category/type
 * @param {Cesium.Cesium3DTileset} tileset - The loaded iTwin tileset
 * @param {string} propertyName - Property name to filter by (e.g., 'category', 'type')
 * @param {string} propertyValue - Property value to match
 * @param {string|Cesium.Color} highlightColor - Optional color (default: GOLD)
 * @example
 * highlightITwinEntitiesByProperty(tileset, 'category', 'Wall', '#FF0000');
 */
export const highlightITwinEntitiesByProperty = (tileset, propertyName, propertyValue, highlightColor) => {
    if (!tileset || !propertyName || !propertyValue) {
        console.warn('Tileset, property name, or property value not provided');
        return;
    }

    const color = highlightColor || window.Cesium.Color.GOLD;
    const colorString = typeof color === 'string' ? color : window.Cesium.Color.toCssColorString(color);

    tileset.style = new window.Cesium.Cesium3DTileStyle({
        color: {
            conditions: [
                [`\${feature['${propertyName}']} === '${propertyValue}'`, `color('${colorString}', 1.0)`],
                ['true', 'color("#FFFFFF", 1.0)']
            ]
        },
        show: true
    });

    console.log(`Highlighted entities where ${propertyName} = ${propertyValue}`);
};

/**
 * Add an entity to current highlights (incremental highlighting)
 * @param {Cesium.Cesium3DTileset} tileset - The loaded iTwin tileset
 * @param {string} entityId - Entity ID to add to highlights
 * @param {string} color - Color for this entity (default: '#FFD700')
 */
export const addToITwinHighlight = (tileset, entityId, color = '#FFD700') => {
    if (!tileset || !entityId) {
        console.warn('Tileset or entity ID not provided');
        return;
    }

    // Get existing highlighted entities
    const existingEntities = window.currentHighlightedEntitiesMap || new Map();
    
    // Add new entity
    existingEntities.set(entityId, color);
    
    // Store updated map
    window.currentHighlightedEntitiesMap = existingEntities;

    // Build conditions
    const conditions = Array.from(existingEntities.entries()).map(([id, clr]) => {
        return [`\${feature['elementId']} === '${id}'`, `color('${clr}', 1.0)`];
    });

    // Add default condition
    conditions.push(['true', 'color("#FFFFFF", 1.0)']);

    tileset.style = new window.Cesium.Cesium3DTileStyle({
        color: {
            conditions: conditions
        },
        show: true
    });

    console.log(`Added entity ${entityId} to highlights. Total highlighted: ${existingEntities.size}`);
};

/**
 * Remove an entity from current highlights
 * @param {Cesium.Cesium3DTileset} tileset - The loaded iTwin tileset
 * @param {string} entityId - Entity ID to remove from highlights
 */
export const removeFromITwinHighlight = (tileset, entityId) => {
    if (!tileset || !entityId) {
        console.warn('Tileset or entity ID not provided');
        return;
    }

    // Get existing highlighted entities
    const existingEntities = window.currentHighlightedEntitiesMap || new Map();
    
    // Remove entity
    existingEntities.delete(entityId);
    
    // Store updated map
    window.currentHighlightedEntitiesMap = existingEntities;

    if (existingEntities.size === 0) {
        // Clear all highlights if no entities left
        clearITwinHighlight(tileset);
        return;
    }

    // Build conditions
    const conditions = Array.from(existingEntities.entries()).map(([id, clr]) => {
        return [`\${feature['elementId']} === '${id}'`, `color('${clr}', 1.0)`];
    });

    // Add default condition
    conditions.push(['true', 'color("#FFFFFF", 1.0)']);

    tileset.style = new window.Cesium.Cesium3DTileStyle({
        color: {
            conditions: conditions
        },
        show: true
    });

    console.log(`Removed entity ${entityId} from highlights. Total highlighted: ${existingEntities.size}`);
};

/**
 * Clears all highlights from the iTwin tileset
 */
export const clearITwinHighlight = (tileset) => {
    if (!tileset) {
        console.warn('Tileset not provided');
        return;
    }

    tileset.style = new window.Cesium.Cesium3DTileStyle({
        color: 'color("#FFFFFF", 1.0)',
        show: true
    });

    window.currentHighlightedEntityId = null;
    window.currentHighlightedEntityIds = [];
    window.currentHighlightedEntitiesMap = new Map();
    console.log('Cleared all highlights');
};

/**
 * Get list of currently highlighted entity IDs
 * @returns {Array<string>} Array of highlighted entity IDs
 */
export const getCurrentHighlightedEntities = () => {
    if (window.currentHighlightedEntitiesMap && window.currentHighlightedEntitiesMap.size > 0) {
        return Array.from(window.currentHighlightedEntitiesMap.keys());
    }
    if (window.currentHighlightedEntityIds && window.currentHighlightedEntityIds.length > 0) {
        return window.currentHighlightedEntityIds;
    }
    if (window.currentHighlightedEntityId) {
        return [window.currentHighlightedEntityId];
    }
    return [];
};

/**
 * Advanced property display with search and filter capabilities
 */
const displayEntityPropertiesAdvanced = (entityId, properties) => {
    console.log('🎨 displayEntityPropertiesAdvanced called');
    console.log('   Entity ID:', entityId);
    console.log('   Properties count:', Object.keys(properties).length);
    
    // ============================================
    // Step 1: Inject CSS (with !important to override everything)
    // ============================================
    const styleId = 'itwin-property-panel-styles';
    if (!document.getElementById(styleId)) {
        console.log('   💅 Injecting CSS styles');
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = `
            .itwin-property-panel {
                position: fixed !important;
                top: 80px !important;
                right: 20px !important;
                width: 380px !important;
                max-height: 70vh !important;
                background: linear-gradient(145deg, rgba(42, 45, 52, 0.98), rgba(32, 35, 42, 0.98)) !important;
                border: 2px solid #00d4ff !important;
                border-radius: 12px !important;
                box-shadow: 
                    0 8px 32px rgba(0, 212, 255, 0.3),
                    0 0 0 1px rgba(0, 212, 255, 0.1) inset !important;
                z-index: 999999 !important;
                overflow: hidden !important;
                display: block !important;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
                backdrop-filter: blur(10px) !important;
            }
            
            .property-panel-header {
                display: flex !important;
                justify-content: space-between !important;
                align-items: center !important;
                padding: 16px 20px !important;
                background: linear-gradient(135deg, #00d4ff 0%, #0099cc 100%) !important;
                color: white !important;
                border-bottom: 1px solid rgba(255, 255, 255, 0.2) !important;
            }
            
            .property-panel-header h3 {
                margin: 0 !important;
                font-size: 17px !important;
                font-weight: 600 !important;
                text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2) !important;
            }
            
            .property-panel-header .close-btn {
                background: rgba(255, 255, 255, 0.2) !important;
                border: none !important;
                color: white !important;
                font-size: 20px !important;
                cursor: pointer !important;
                padding: 4px !important;
                width: 28px !important;
                height: 28px !important;
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
                border-radius: 6px !important;
                transition: all 0.2s ease !important;
            }
            
            .property-panel-header .close-btn:hover {
                background: rgba(255, 255, 255, 0.3) !important;
                transform: scale(1.1) !important;
            }
            
            .property-search {
                padding: 12px 16px !important;
                background: rgba(0, 0, 0, 0.3) !important;
                border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
            }
            
            .property-search input {
                width: 100% !important;
                padding: 10px 14px !important;
                background: rgba(255, 255, 255, 0.08) !important;
                border: 1px solid rgba(0, 212, 255, 0.3) !important;
                border-radius: 8px !important;
                color: white !important;
                font-size: 13px !important;
                transition: all 0.2s ease !important;
                box-sizing: border-box !important;
            }
            
            .property-search input:focus {
                outline: none !important;
                border-color: #00d4ff !important;
                background: rgba(255, 255, 255, 0.12) !important;
                box-shadow: 0 0 0 3px rgba(0, 212, 255, 0.1) !important;
            }
            
            .property-search input::placeholder {
                color: rgba(255, 255, 255, 0.4) !important;
            }
            
            .property-panel-content {
                padding: 16px !important;
                max-height: calc(70vh - 220px) !important;
                overflow-y: auto !important;
                color: white !important;
            }
            
            .property-panel-content::-webkit-scrollbar {
                width: 8px !important;
            }
            
            .property-panel-content::-webkit-scrollbar-track {
                background: rgba(0, 0, 0, 0.2) !important;
                border-radius: 4px !important;
            }
            
            .property-panel-content::-webkit-scrollbar-thumb {
                background: linear-gradient(180deg, #00d4ff 0%, #0099cc 100%) !important;
                border-radius: 4px !important;
            }
            
            .property-panel-content::-webkit-scrollbar-thumb:hover {
                background: #00d4ff !important;
            }
            
            .property-item {
                display: flex !important;
                justify-content: space-between !important;
                align-items: flex-start !important;
                padding: 12px 0 !important;
                border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
                transition: background 0.2s ease !important;
            }
            
            .property-item:hover {
                background: rgba(0, 212, 255, 0.05) !important;
                padding-left: 8px !important;
                padding-right: 8px !important;
                margin-left: -8px !important;
                margin-right: -8px !important;
                border-radius: 6px !important;
            }
            
            .property-item:last-child {
                border-bottom: none !important;
            }
            
            .property-item strong {
                color: #00d4ff !important;
                margin-right: 12px !important;
                min-width: 140px !important;
                font-size: 13px !important;
                font-weight: 600 !important;
            }
            
            .property-item span {
                color: rgba(255, 255, 255, 0.9) !important;
                font-size: 13px !important;
                word-break: break-word !important;
                text-align: right !important;
                flex: 1 !important;
                line-height: 1.5 !important;
            }
            
            .property-panel-footer {
                display: flex !important;
                gap: 10px !important;
                padding: 16px !important;
                background: rgba(0, 0, 0, 0.3) !important;
                border-top: 1px solid rgba(255, 255, 255, 0.1) !important;
            }
            
            .property-panel-footer button {
                flex: 1 !important;
                padding: 10px 16px !important;
                background: linear-gradient(135deg, #00d4ff 0%, #0099cc 100%) !important;
                border: none !important;
                border-radius: 8px !important;
                color: white !important;
                font-size: 13px !important;
                font-weight: 600 !important;
                cursor: pointer !important;
                transition: all 0.2s ease !important;
                box-shadow: 0 2px 8px rgba(0, 212, 255, 0.3) !important;
            }
            
            .property-panel-footer button:hover {
                transform: translateY(-2px) !important;
                box-shadow: 0 4px 12px rgba(0, 212, 255, 0.5) !important;
            }
            
            .property-panel-footer button:active {
                transform: translateY(0) !important;
            }
        `;
        document.head.appendChild(style);
        console.log('   ✅ CSS styles injected');
    }
    
    // ============================================
    // Step 2: Find or create container
    // ============================================
    const twinApp = document.querySelector('twinworld-app');
    let container;
    
    // Try shadow root first
    if (twinApp && twinApp.shadowRoot) {
        container = twinApp.shadowRoot;
        console.log('   📦 Using shadow root container');
    } else {
        container = document.body;
        console.log('   📦 Using document.body container');
    }
    
    // ============================================
    // Step 3: Create or reuse panel
    // ============================================
    let propertyPanel = container.querySelector('.itwin-property-panel');
    
    if (!propertyPanel) {
        console.log('   🆕 Creating new property panel');
        propertyPanel = document.createElement('div');
        propertyPanel.className = 'itwin-property-panel';
        container.appendChild(propertyPanel);
        console.log('   ✅ Panel appended to:', container === document.body ? 'body' : 'shadow root');
    } else {
        console.log('   ♻️ Reusing existing property panel');
    }
    
    // ============================================
    // Step 4: Build HTML content
    // ============================================
    let propertiesHTML = `
        <div class="property-panel-header">
            <h3>🔍 Entity Properties</h3>
            <button class="close-btn" onclick="window.hideEntityProperties()">×</button>
        </div>
        <div class="property-search">
            <input 
                type="text" 
                id="propertySearch" 
                placeholder="🔎 Search properties..." 
                onkeyup="window.filterProperties()"
            />
        </div>
        <div class="property-panel-content" id="propertyContent">
            <div class="property-item">
                <strong>Entity ID:</strong>
                <span>${entityId}</span>
            </div>
    `;
    
    // Separate system properties from user properties
    const systemProps = {};
    const userProps = {};
    
    for (const [key, value] of Object.entries(properties)) {
        if (key.startsWith('_')) {
            systemProps[key] = value;
        } else {
            userProps[key] = value;
        }
    }
    
    // Add user properties first
    for (const [key, value] of Object.entries(userProps)) {
        propertiesHTML += `
            <div class="property-item" data-key="${key.toLowerCase()}">
                <strong>${key}:</strong>
                <span>${formatPropertyValue(value)}</span>
            </div>
        `;
    }
    
    // Add system properties at the end (if any)
    if (Object.keys(systemProps).length > 0) {
        propertiesHTML += `
            <div class="property-item" style="margin-top: 12px; padding-top: 12px; border-top: 2px solid rgba(255,255,255,0.1);">
                <strong style="color: rgba(255,255,255,0.5);">System Info</strong>
                <span></span>
            </div>
        `;
        
        for (const [key, value] of Object.entries(systemProps)) {
            propertiesHTML += `
                <div class="property-item" data-key="${key.toLowerCase()}">
                    <strong style="color: rgba(255,255,255,0.5);">${key}:</strong>
                    <span style="color: rgba(255,255,255,0.5);">${formatPropertyValue(value)}</span>
                </div>
            `;
        }
    }
    
    propertiesHTML += `
        </div>
        <div class="property-panel-footer">
            <button onclick="window.exportITwinProperties('${entityId}')">
                📥 Export
            </button>
            <button onclick="window.copyEntityId('${entityId}')">
                📋 Copy ID
            </button>
        </div>
    `;
    
    propertyPanel.innerHTML = propertiesHTML;
    
    // ============================================
    // Step 5: Force visibility
    // ============================================
    propertyPanel.style.cssText = `
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
        pointer-events: auto !important;
    `;
    
    console.log('   ✅ Property panel HTML updated');
    console.log('   📊 Panel element:', propertyPanel);
    console.log('   📏 Panel dimensions:', propertyPanel.getBoundingClientRect());
    console.log('   👁️  Panel computed display:', window.getComputedStyle(propertyPanel).display);
    console.log('   🎨 Panel parent:', propertyPanel.parentElement);
    
    window.currentEntityProperties = { entityId, properties };
};

/**
 * Filter properties based on search input
 */
export const filterProperties = () => {
    const twinApp = document.querySelector('twinworld-app');
    const container = twinApp && twinApp.shadowRoot ? twinApp.shadowRoot : document;
    
    const searchInput = container.querySelector('#propertySearch');
    if (!searchInput) return;
    
    const filter = searchInput.value.toLowerCase();
    const propertyItems = container.querySelectorAll('.property-item');
    
    propertyItems.forEach(item => {
        const key = item.getAttribute('data-key') || '';
        const text = item.textContent.toLowerCase();
        
        if (text.includes(filter)) {
            item.style.display = 'flex';
        } else {
            item.style.display = 'none';
        }
    });
};

/**
 * Export properties as JSON
 */
export const exportITwinProperties = (entityId) => {
    if (!window.currentEntityProperties) {
        console.error('No properties to export');
        return;
    }
    
    const dataStr = JSON.stringify(window.currentEntityProperties, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `entity_${entityId}_properties.json`;
    link.click();
    
    URL.revokeObjectURL(url);
    
    console.log('Properties exported successfully');
};

/**
 * Copy entity ID to clipboard
 */
export const copyEntityId = (entityId) => {
    navigator.clipboard.writeText(entityId).then(() => {
        window.showAlert && window.showAlert(`Entity ID copied: ${entityId}`);
        console.log(`Entity ID copied: ${entityId}`);
    }).catch(err => {
        console.error('Failed to copy entity ID:', err);
    });
};

/**
 * Hide the property panel
 */
export const hideEntityProperties = () => {
    const twinApp = document.querySelector('twinworld-app');
    const container = twinApp && twinApp.shadowRoot ? twinApp.shadowRoot : document;
    
    const propertyPanel = container.querySelector('.itwin-property-panel');
    if (propertyPanel) {
        propertyPanel.style.display = 'none';
    }
};

/**
 * Setup interactive click handler for iTwin entities
 * Handles both batched and non-batched 3D tiles, plus various property access methods
 */
export const setupITwinClickHandler = (viewer, tileset) => {
    console.log('🔧 Setting up iTwin click handler');
    console.log('   Viewer:', viewer);
    console.log('   Tileset:', tileset);
    
    if (window.itwinClickHandler) {
        console.log('   Destroying existing handler');
        window.itwinClickHandler.destroy();
    }
    
    const handler = new window.Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);

    handler.setInputAction((movement) => {
        console.log('🖱️ Click detected at:', movement.position);
        
        const pickedObject = viewer.scene.pick(movement.position);
        console.log('   Picked object:', pickedObject);
        
        if (!window.Cesium.defined(pickedObject)) {
            console.log('   ℹ️ Clicked on empty space');
            restorePreviousHighlight();
            hideEntityProperties();
            return;
        }
        
        let properties = {};
        let entityId = null;
        let canHighlight = false;
        
        // ============================================
        // CASE 1: Cesium3DTileFeature (batched or non-batched)
        // ============================================
        if (pickedObject instanceof window.Cesium.Cesium3DTileFeature) {
            console.log('   ✅ Case 1: Cesium3DTileFeature detected');
            
            const feature = pickedObject;
            canHighlight = true;
            
            // Get batch ID (for batched models)
            const batchId = feature._batchId;
            if (batchId !== undefined) {
                console.log('      Batched feature - Batch ID:', batchId);
            }
            
            // ============================================
            // Strategy 1: Public API (Most Reliable)
            // ============================================
            console.log('   📋 Strategy 1: Public API methods');
            try {
                // Method A: getPropertyIds
                if (typeof feature.getPropertyIds === 'function') {
                    const propertyIds = feature.getPropertyIds();
                    console.log('      Property IDs:', propertyIds);
                    
                    if (propertyIds && propertyIds.length > 0) {
                        propertyIds.forEach(id => {
                            try {
                                const value = feature.getProperty(id);
                                if (value !== undefined && value !== null) {
                                    properties[id] = value;
                                    console.log(`         ${id} =`, value);
                                }
                            } catch (err) {
                                console.warn(`         Failed to get property '${id}':`, err);
                            }
                        });
                    }
                }
                // Method B: getPropertyNames
                else if (typeof feature.getPropertyNames === 'function') {
                    const propertyNames = feature.getPropertyNames();
                    console.log('      Property names:', propertyNames);
                    
                    if (propertyNames && propertyNames.length > 0) {
                        propertyNames.forEach(name => {
                            try {
                                const value = feature.getProperty(name);
                                if (value !== undefined && value !== null) {
                                    properties[name] = value;
                                    console.log(`         ${name} =`, value);
                                }
                            } catch (err) {
                                console.warn(`         Failed to get property '${name}':`, err);
                            }
                        });
                    }
                }
            } catch (error) {
                console.warn('      Public API methods failed:', error);
            }
            
            // ============================================
            // Strategy 2: Direct Batch Table Access (Deep Dive)
            // ============================================
            if (batchId !== undefined && feature._content) {
                console.log('   📋 Strategy 2: Batch table deep inspection');
                
                const content = feature._content;
                console.log('      Content keys:', Object.keys(content));
                
                // Check for batch table
                if (content.batchTable) {
                    const batchTable = content.batchTable;
                    console.log('      Batch table found:', batchTable);
                    console.log('      Batch table keys:', Object.keys(batchTable));
                    
                    // Method A: _properties (internal structure)
                    if (batchTable._properties) {
                        console.log('      Batch table _properties keys:', Object.keys(batchTable._properties));
                        
                        for (const propName in batchTable._properties) {
                            try {
                                const propArray = batchTable._properties[propName];
                                console.log(`         Checking property '${propName}':`, {
                                    isArray: Array.isArray(propArray),
                                    length: propArray ? propArray.length : 0,
                                    type: typeof propArray
                                });
                                
                                if (propArray && propArray[batchId] !== undefined) {
                                    const value = propArray[batchId];
                                    properties[propName] = value;
                                    console.log(`         ✅ ${propName} = ${value}`);
                                }
                            } catch (err) {
                                console.warn(`         Failed to access '${propName}':`, err);
                            }
                        }
                    }
                    
                    // Method B: properties (alternative structure)
                    if (batchTable.properties) {
                        console.log('      Batch table .properties found');
                        for (const propName in batchTable.properties) {
                            try {
                                const prop = batchTable.properties[propName];
                                if (prop && prop[batchId] !== undefined) {
                                    properties[propName] = prop[batchId];
                                    console.log(`         ✅ ${propName} = ${prop[batchId]}`);
                                }
                            } catch (err) {
                                console.warn(`         Failed to access '${propName}':`, err);
                            }
                        }
                    }
                    
                    // Method C: Try public batch table methods
                    if (typeof batchTable.getProperty === 'function') {
                        console.log('      Batch table has getProperty method');
                        // Try common property names
                        const commonProps = [
                            'elementId', 'ElementId', 'element_id',
                            'name', 'Name',
                            'category', 'Category',
                            'material', 'Material',
                            'type', 'Type',
                            'class', 'Class',
                            'layer', 'Layer'
                        ];
                        
                        commonProps.forEach(prop => {
                            try {
                                const value = batchTable.getProperty(batchId, prop);
                                if (value !== undefined && value !== null) {
                                    properties[prop] = value;
                                    console.log(`         ✅ ${prop} = ${value}`);
                                }
                            } catch (err) {
                                // Silently skip
                            }
                        });
                    }
                }
                
                // Check for feature table (alternative to batch table)
                if (content.featureTable) {
                    console.log('      Feature table found:', content.featureTable);
                    const featureTable = content.featureTable;
                    
                    if (featureTable._properties) {
                        console.log('      Feature table properties:', Object.keys(featureTable._properties));
                        for (const propName in featureTable._properties) {
                            try {
                                const propArray = featureTable._properties[propName];
                                if (propArray && propArray[batchId] !== undefined) {
                                    properties[propName] = propArray[batchId];
                                    console.log(`         ✅ ${propName} = ${propArray[batchId]}`);
                                }
                            } catch (err) {
                                console.warn(`         Failed to access '${propName}':`, err);
                            }
                        }
                    }
                }
            }
            
            // ============================================
            // Strategy 3: Feature Metadata (3D Tiles 1.1+)
            // ============================================
            console.log('   📋 Strategy 3: Feature metadata');
            if (feature.getPropertyIds) {
                try {
                    const ids = feature.getPropertyIds();
                    console.log('      Metadata property IDs:', ids);
                    ids.forEach(id => {
                        if (!properties[id]) { // Don't overwrite existing
                            const value = feature.getProperty(id);
                            if (value !== undefined && value !== null) {
                                properties[id] = value;
                                console.log(`         ✅ ${id} = ${value}`);
                            }
                        }
                    });
                } catch (err) {
                    console.warn('      Metadata extraction failed:', err);
                }
            }
            
            // ============================================
            // Add System Properties
            // ============================================
            if (batchId !== undefined) {
                properties['_Batch ID'] = batchId;
            }
            if (feature._model && feature._model._resource) {
                properties['_Model URL'] = feature._model._resource._url;
            }
            if (feature._tile) {
                properties['_Tile Depth'] = feature._tile._depth || 'unknown';
            }
            
        }
        // ============================================
        // CASE 2-4: Other object types (unchanged)
        // ============================================
        else if (pickedObject.primitive && pickedObject.primitive instanceof window.Cesium.Cesium3DTileset) {
            console.log('   ✅ Case 2: Cesium3DTileset primitive (instanced)');
            properties['Instance ID'] = pickedObject.instanceId || 'unknown';
            properties['Primitive Type'] = 'Cesium3DTileset';
            canHighlight = false;
        }
        else if (pickedObject.id) {
            console.log('   ✅ Case 3: Entity or Model with ID');
            const entity = pickedObject.id;
            
            if (entity.properties) {
                const propNames = entity.properties.propertyNames;
                if (propNames) {
                    propNames.forEach(name => {
                        try {
                            properties[name] = entity.properties[name].getValue();
                        } catch (err) {
                            properties[name] = entity.properties[name];
                        }
                    });
                }
            }
            
            if (entity.name) properties['Name'] = entity.name;
            if (entity.id) properties['Entity ID'] = entity.id;
            canHighlight = false;
        }
        else {
            console.log('   ⚠️ Case 4: Unknown object type');
            properties['Object Type'] = pickedObject.constructor.name;
            properties['Available Keys'] = Object.keys(pickedObject).join(', ');
            canHighlight = false;
        }
        
        // ============================================
        // Determine Entity ID (prefer meaningful IDs over batch ID)
        // ============================================
        const possibleIdFields = [
            'element',               // ⭐⭐⭐ THIS IS THE ONE! (iTwin ECInstanceId)
            'ECInstanceId',          
            'elementId',              
            'ElementId',              
            'id', 'ID', 'Id',        
            'name', 'Name', 'NAME',  
            '_Batch ID'              // Fallback only
        ];

        for (const field of possibleIdFields) {
            if (properties[field] !== undefined && properties[field] !== null && properties[field] !== '') {
                // Convert BigInt to string for entityId
                entityId = typeof properties[field] === 'bigint' 
                    ? properties[field].toString() 
                    : String(properties[field]);
                console.log(`   ✅ Using '${field}' as entity ID:`, entityId);
                break;
            }
        }
        
        // Fallback: generate ID
        if (!entityId) {
            entityId = `feature_${Date.now()}`;
            console.log('   ⚠️ No ID found, using generated ID:', entityId);
        }
        
        console.log('   📋 Final properties (count: ' + Object.keys(properties).length + '):', properties);
        console.log('   🎯 Can highlight:', canHighlight);
        
        // ============================================
        // Highlighting
        // ============================================
        if (canHighlight && pickedObject instanceof window.Cesium.Cesium3DTileFeature) {
            try {
                restorePreviousHighlight();
                
                if (!pickedObject._originalColor) {
                    pickedObject._originalColor = pickedObject.color 
                        ? window.Cesium.Color.clone(pickedObject.color) 
                        : window.Cesium.Color.WHITE.clone();
                }
                
                // pickedObject.color = window.Cesium.Color.GOLD;
                pickedObject.color = window.Cesium.Color.fromCssColorString('#00ffff').withAlpha(0.9);
                window.currentHighlightedFeature = pickedObject;
                
                console.log('   ✅ Feature highlighted');
            } catch (error) {
                console.error('   ❌ Highlighting failed:', error);
            }
        }
        
        // ============================================
        // Display Properties Panel
        // ============================================
        if (Object.keys(properties).length === 0) {
            console.warn('   ⚠️ No properties found! This is unusual.');
            properties['Warning'] = 'No properties could be extracted from this feature';
            properties['Batch ID'] = pickedObject._batchId || 'N/A';
        }
        
        console.log('   📺 Displaying properties panel...');
        displayEntityPropertiesAdvanced(entityId, properties);
        
        // Verify panel creation
        setTimeout(() => {
            const panel = document.querySelector('.itwin-property-panel') || 
                         document.querySelector('twinworld-app')?.shadowRoot?.querySelector('.itwin-property-panel');
            console.log('   Panel check:', panel ? '✅ Panel visible' : '❌ Panel NOT found');
            if (panel) {
                const rect = panel.getBoundingClientRect();
                console.log('      Panel position:', { top: rect.top, right: rect.right, width: rect.width, height: rect.height });
            }
        }, 100);
        
    }, window.Cesium.ScreenSpaceEventType.LEFT_CLICK);
    
    window.itwinClickHandler = handler;
    console.log('✅ Click handler registered successfully');
};

/**
 * Restore previously highlighted feature to original color
 */
function restorePreviousHighlight() {
    if (window.currentHighlightedFeature) {
        try {
            if (window.currentHighlightedFeature._originalColor) {
                window.currentHighlightedFeature.color = window.currentHighlightedFeature._originalColor;
            }
            window.currentHighlightedFeature = null;
            console.log('   🔄 Previous highlight restored');
        } catch (error) {
            console.error('   ❌ Error restoring highlight:', error);
        }
    }
}

/**
 * Highlights specific entities with transparency for others and shows tooltips on hover
 * @param {Cesium.Cesium3DTileset} tileset - The loaded iTwin tileset
 * @param {Array<string>} entityIds - Array of entity IDs to highlight
 * @param {string|Cesium.Color} highlightColor - Color to highlight the specified entities
 * @param {Array<Object>} features - Array of feature objects with entityId and tooltip data
 *   Example: [{ entityId: '123', name: 'Wall A', category: 'Structure', ... }, ...]
 * @param {number} transparencyLevel - Transparency level for non-highlighted elements (0.0 to 1.0, default: 0.2)
 * @param {Cesium.Viewer} viewer - Cesium viewer instance for tooltip rendering
 * @example
 * highlightMultipleITwinEntitiesWithTransparencyAndTooltip(
 *   tileset, 
 *   ['0x123', '0x456'], 
 *   '#FF0000', 
 *   [
 *     { entityId: '0x123', name: 'Wall A', category: 'Structure', height: '10m' },
 *     { entityId: '0x456', name: 'Column B', category: 'Support', material: 'Steel' }
 *   ],
 *   0.2,
 *   viewer
 * );
 */
export const highlightMultipleITwinEntitiesWithTransparencyAndTooltip = (
    tileset, 
    entityIds, 
    highlightColor, 
    features, 
    transparencyLevel,
    viewer
) => {
    if (!tileset || !entityIds || entityIds.length === 0) {
        console.warn('❌ Tileset or entity IDs not provided');
        return;
    }

    // Validate and normalize parameters
    if (!features) {
        console.warn('⚠️  Features parameter is undefined, using empty array');
        features = [];
    }
    
    if (!Array.isArray(features)) {
        console.warn('⚠️  Features is not an array, converting:', features);
        // Try to convert object to array
        if (typeof features === 'object') {
            features = Object.values(features);
        } else {
            features = [];
        }
    }

    if (transparencyLevel === undefined || transparencyLevel === null) {
        transparencyLevel = 0.2;
    }

    console.log(`🎨 Highlighting ${entityIds.length} entities with transparency and tooltips`);
    console.log(`📋 Features provided: ${features.length}`, features);
    console.log(`👁️  Viewer provided:`, viewer ? '✅ YES' : '❌ NO');
    console.log(`🌫️  Transparency level:`, transparencyLevel);

    // Parse highlight color
    let cesiumHighlightColor;
    if (typeof highlightColor === 'string') {
        cesiumHighlightColor = window.Cesium.Color.fromCssColorString(highlightColor);
    } else {
        cesiumHighlightColor = highlightColor || window.Cesium.Color.fromCssColorString('#00d4ff');
    }

    // Create transparent gray color for non-highlighted elements
    const transparentColor = new window.Cesium.Color(0.9, 0.95, 1.0, 0.15);

    // Convert entity IDs to Set for fast lookup
    const entityIdSet = new Set(
        entityIds.map(id => {
            const normalized = normalizeEntityId(id);
            return normalized.asString;
        })
    );

    // Create a map of entityId -> feature data for tooltips
    const featureMap = new Map();
    
    if (features && features.length > 0) {
        features.forEach((feature, index) => {
            if (!feature) {
                console.warn(`   ⚠️  Feature at index ${index} is null/undefined`);
                return;
            }
            
            if (feature.entityId) {
                const normalized = normalizeEntityId(feature.entityId);
                featureMap.set(normalized.asString, feature);
                console.log(`   📌 Mapped feature ${index}: ${normalized.asString}`, feature);
            } else {
                console.warn(`   ⚠️  Feature at index ${index} missing entityId:`, feature);
            }
        });
    }

    console.log('   Entity ID set:', Array.from(entityIdSet));
    console.log('   Feature map keys:', Array.from(featureMap.keys()));
    console.log('   Feature map size:', featureMap.size);

    let highlightedCount = 0;
    let fadedCount = 0;
    let checkedFeaturesCount = 0;

    // Store feature references for hover interaction
    const highlightedFeatures = [];

    // Function to process a tile's features
    const processTile = (tile) => {
        if (!tile.content || !tile.content.featuresLength) {
            return;
        }

        const content = tile.content;
        const featuresLength = content.featuresLength;

        for (let i = 0; i < featuresLength; i++) {
            checkedFeaturesCount++;
            
            try {
                const feature = content.getFeature(i);
                
                if (!feature) continue;

                // Store original color if not already stored
                if (!feature._originalColor) {
                    feature._originalColor = feature.color 
                        ? window.Cesium.Color.clone(feature.color) 
                        : window.Cesium.Color.WHITE.clone();
                }

                // Get element ID from feature
                let featureElementId = null;
                
                if (typeof feature.getProperty === 'function') {
                    // Try to get 'element' property
                    try {
                        const elementProp = feature.getProperty('element');
                        if (elementProp !== undefined && elementProp !== null) {
                            featureElementId = typeof elementProp === 'bigint' 
                                ? elementProp.toString() 
                                : String(elementProp);
                        }
                    } catch (err) {
                        // Try alternative property names
                        try {
                            const altProp = feature.getProperty('ECInstanceId') || feature.getProperty('elementId');
                            if (altProp !== undefined && altProp !== null) {
                                featureElementId = typeof altProp === 'bigint' 
                                    ? altProp.toString() 
                                    : String(altProp);
                            }
                        } catch (err2) {
                            // Silent fail
                        }
                    }
                }

                // Check if this feature should be highlighted or made transparent
                if (featureElementId && entityIdSet.has(featureElementId)) {
                    // Highlight this feature
                    feature.color = cesiumHighlightColor;
                    
                    // IMPORTANT: Store custom property for tooltip lookup
                    feature._entityId = featureElementId;
                    
                    highlightedCount++;
                    
                    // Store feature reference with its data for tooltip
                    const featureData = featureMap.get(featureElementId);
                    if (featureData) {
                        highlightedFeatures.push({
                            feature: feature,
                            entityId: featureElementId,
                            data: featureData
                        });
                        console.log(`   ✅ Highlighted feature with tooltip data: ${featureElementId}`);
                    } else {
                        console.log(`   ⚠️  Highlighted feature WITHOUT tooltip data: ${featureElementId}`);
                    }
                } else {
                    // Make this feature transparent and colorless
                    feature.color = transparentColor;
                    fadedCount++;
                }

            } catch (error) {
                // Skip features that can't be accessed
            }
        }
    };

    // Process root tile
    if (tileset.root) {
        processTile(tileset.root);

        // Process child tiles recursively
        const processChildren = (tile) => {
            if (tile.children && tile.children.length > 0) {
                tile.children.forEach(child => {
                    processTile(child);
                    processChildren(child);
                });
            }
        };

        processChildren(tileset.root);
    }

    console.log(`   Checked ${checkedFeaturesCount} features`);
    console.log(`   ✅ Successfully highlighted ${highlightedCount} features`);
    console.log(`   🌫️  Made ${fadedCount} features transparent`);
    console.log(`   💬 Features with tooltip data: ${highlightedFeatures.length}`);

    if (highlightedCount === 0) {
        console.warn('   ⚠️ No features were highlighted. Possible reasons:');
        console.warn('      - Entity IDs do not match any loaded features');
        console.warn('      - Tiles containing those features are not loaded yet');
        console.warn('      - Property name is different');
    }

    // ============================================
    // Setup Hover Tooltip
    // ============================================
    if (viewer && featureMap.size > 0) {
        setupTooltipHandler(viewer, tileset, featureMap);
    } else {
        if (!viewer) {
            console.warn('⚠️  Viewer not provided - tooltips will not work!');
            console.warn('   💡 Make sure to pass the Cesium viewer as the 6th parameter');
        }
        if (featureMap.size === 0) {
            console.warn('⚠️  No features with tooltip data - tooltips will not work!');
        }
    }

    return { highlightedCount, fadedCount, highlightedFeatures, featureMap };
};

/**
 * Setup hover tooltip handler for highlighted entities
 * @param {Cesium.Viewer} viewer - Cesium viewer instance
 * @param {Cesium.Cesium3DTileset} tileset - The loaded iTwin tileset
 * @param {Map} featureMap - Map of entityId -> feature data
 */
const setupTooltipHandler = (viewer, tileset, featureMap) => {
    console.log('🖱️  Setting up tooltip hover handler');
    console.log('   Feature map size:', featureMap.size);
    console.log('   Feature map keys:', Array.from(featureMap.keys()));

    // Create tooltip element if it doesn't exist
    let tooltipElement = document.getElementById('itwin-hover-tooltip');
    if (!tooltipElement) {
        tooltipElement = document.createElement('div');
        tooltipElement.id = 'itwin-hover-tooltip';
        tooltipElement.style.cssText = `
            position: fixed !important;
            display: none !important;
            background: linear-gradient(145deg, rgba(42, 45, 52, 0.98), rgba(32, 35, 42, 0.98)) !important;
            border: 2px solid #00d4ff !important;
            border-radius: 8px !important;
            padding: 12px 16px !important;
            color: white !important;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
            font-size: 13px !important;
            z-index: 1000000 !important;
            pointer-events: none !important;
            max-width: 300px !important;
            min-width: 200px !important;
            box-shadow: 0 4px 16px rgba(0, 212, 255, 0.4) !important;
            backdrop-filter: blur(10px) !important;
        `;
        document.body.appendChild(tooltipElement);
        console.log('   ✅ Tooltip element created and added to body');
    } else {
        console.log('   ♻️  Reusing existing tooltip element');
    }

    // Remove existing handler if present
    if (window.itwinTooltipHandler) {
        console.log('   🔄 Destroying existing tooltip handler');
        window.itwinTooltipHandler.destroy();
    }

    // Create hover handler
    const handler = new window.Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);

    let hoverCheckCount = 0;
    let lastLoggedFeature = null;

    handler.setInputAction((movement) => {
        hoverCheckCount++;
        
        const pickedObject = viewer.scene.pick(movement.endPosition);
        
        if (window.Cesium.defined(pickedObject) && 
            pickedObject instanceof window.Cesium.Cesium3DTileFeature) {
            
            // Get element ID from the picked feature
            let featureElementId = null;
            
            // Method 1: Check if we stored it
            if (pickedObject._entityId) {
                featureElementId = pickedObject._entityId;
            }
            
            // Method 2: Query properties
            if (!featureElementId && typeof pickedObject.getProperty === 'function') {
                try {
                    const elementProp = pickedObject.getProperty('element');
                    if (elementProp !== undefined && elementProp !== null) {
                        featureElementId = typeof elementProp === 'bigint' 
                            ? elementProp.toString() 
                            : String(elementProp);
                    }
                } catch (err) {
                    try {
                        const altProp = pickedObject.getProperty('ECInstanceId') || 
                                       pickedObject.getProperty('elementId');
                        if (altProp !== undefined && altProp !== null) {
                            featureElementId = typeof altProp === 'bigint' 
                                ? altProp.toString() 
                                : String(altProp);
                        }
                    } catch (err2) {
                        // Silent fail
                    }
                }
            }

            // Only log when we find a different feature
            if (featureElementId && featureElementId !== lastLoggedFeature) {
                // console.log('   🎯 Hovering over feature:', featureElementId);
                // console.log('   🗺️  Has tooltip data?', featureMap.has(featureElementId));
                lastLoggedFeature = featureElementId;
            }

            // Check if this feature has tooltip data
            if (featureElementId && featureMap.has(featureElementId)) {
                const featureData = featureMap.get(featureElementId);
                
                // Build tooltip HTML
                let tooltipHTML = '<div style="font-weight: 600; margin-bottom: 8px; color: #00d4ff; font-size: 14px;">📍 Feature Info</div>';
                for (const [key, value] of Object.entries(featureData)) {
                    if (key !== 'entityId') {  // Skip entityId in tooltip
                        tooltipHTML += `
                            <div style="margin-bottom: 6px; line-height: 1.4;">
                                <strong style="color: #00d4ff; margin-right: 8px;">${key}:</strong>
                                <span style="color: rgba(255, 255, 255, 0.9);">${formatPropertyValue(value)}</span>
                            </div>
                        `;
                    }
                }

                tooltipElement.innerHTML = tooltipHTML;
                tooltipElement.style.display = 'block';
                tooltipElement.style.left = (movement.endPosition.x + 15) + 'px';
                tooltipElement.style.top = (movement.endPosition.y + 15) + 'px';
                
                return;
            }
        } else {
            // Reset last logged feature when not hovering
            if (lastLoggedFeature !== null) {
                lastLoggedFeature = null;
            }
        }

        // Hide tooltip if nothing is hovered
        tooltipElement.style.display = 'none';
        
    }, window.Cesium.ScreenSpaceEventType.MOUSE_MOVE);

    window.itwinTooltipHandler = handler;
    console.log('   ✅ Tooltip handler registered');
    
    // Store featureMap globally for debugging
    window._debugFeatureMap = featureMap;
    console.log('   🔧 Debug: window._debugFeatureMap available for inspection');
};

/**
 * Clear tooltip handler and remove tooltip element
 */
export const clearTooltipHandler = () => {
    if (window.itwinTooltipHandler) {
        window.itwinTooltipHandler.destroy();
        window.itwinTooltipHandler = null;
        console.log('🔄 Tooltip handler destroyed');
    }
    
    const tooltipElement = document.getElementById('itwin-hover-tooltip');
    if (tooltipElement) {
        tooltipElement.remove();
        console.log('🔄 Tooltip element removed');
    }
    
    if (window._debugFeatureMap) {
        delete window._debugFeatureMap;
    }
};

/**
 * Initialize all iTwin utilities as global functions
 */
export const initITwinUtils = () => {
    window.highlightITwinEntity = highlightITwinEntity;
    window.highlightMultipleITwinEntities = highlightMultipleITwinEntities;
    window.highlightMultipleITwinEntitiesSameColor = highlightMultipleITwinEntitiesSameColor;
    window.highlightMultipleITwinEntitiesSameColordirect = highlightMultipleITwinEntitiesSameColordirect;
    window.highlightMultipleITwinEntitiesDirect = highlightMultipleITwinEntitiesDirect; // NEW
    window.clearITwinDirectHighlight = clearITwinDirectHighlight; // NEW
    window.highlightMultipleITwinEntitiesWithTransparency = highlightMultipleITwinEntitiesWithTransparency; // NEW
    window.restoreOriginalITwinColors = restoreOriginalITwinColors; // NEW
    window.highlightMultipleITwinEntitiesByProperty = highlightITwinEntitiesByProperty;
    window.addToITwinHighlight = addToITwinHighlight;
    window.removeFromITwinHighlight = removeFromITwinHighlight;
    window.clearITwinHighlight = clearITwinHighlight;
    window.getCurrentHighlightedEntities = getCurrentHighlightedEntities;
    window.filterProperties = filterProperties;
    window.exportITwinProperties = exportITwinProperties;
    window.copyEntityId = copyEntityId;
    window.hideEntityProperties = hideEntityProperties;
    window.setupITwinClickHandler = setupITwinClickHandler;
    window.highlightMultipleITwinEntitiesWithTransparencyAndTooltip = highlightMultipleITwinEntitiesWithTransparencyAndTooltip;
    window.clearTooltipHandler = clearTooltipHandler;
    
    console.log('iTwin utilities initialized');
};